'use client';

/**
 * Campaign-creation entry point.
 *
 * Two layouts share this route. Outside test mode the picker matches the pre-#4277-chunk-4b
 * production surface — a single-column list of Google campaign types (Brand / Prospecting / HiPo /
 * Competitor) with direct click-to-navigate behaviour. ICP, Location and any Meta surface stay
 * hidden because the Meta wizard is still pre-GA per [[feedback_frontend_gating_pattern]] and the
 * remaining Google variants are still test-mode features.
 *
 * Test mode renders a platform-first picker: a Google / Meta tab bar over the campaign-type list,
 * single-select via radio. The Google tab carries the full prod set plus the test-mode-only ICP /
 * Location rows; the Meta tab mirrors the backend phase mapping — Prospecting, HiPo, Retargeting
 * (no Brand or Competitor campaign exists on Meta). Selecting a type then CONTINUE dispatches to the
 * matching wizard (the Meta phase mapping is the only translation step).
 *
 * Selection survives back-navigation through ``campaignCreationPreSelectionAtom.types`` and the
 * platform tab through ``selectedNewCampaignPlatformAtom`` (#5975 — the row id alone can't restore
 * the tab, Prospecting/HiPo exist on both platforms). Each picker filters the atom value down to
 * entries it knows about, so a stale id left by the other layout just falls back to "nothing
 * selected" instead of throwing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useWizardHost } from '@/components/campaign/new/wizard-host-context';
import { CampaignTypeOption, TYPE_META, type CampaignTypeRowId } from '@/components/campaign/new/campaign-type-option';
import { TabCardPanel, tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { SelectGoogleAdsAccount } from '@/components/campaign/onboarding/select-googleads-acccount';
import TrackingIssues from '@/components/TrackingIssues';
import { usePlatformConnections } from '@/hooks/usePlatformConnections';
import { useMetaIntegrationStatus } from '@/lib/query-options';
import { shouldAutoOpenMetaAccountPicker } from '@/lib/meta';
import { useTestMode } from '@/hooks/useTestMode';
import { MetaAdAccountSelectionModal } from '@/components/glitch-ui/meta-ad-account-selection-modal';
import { PlatformConnectionBanner } from '@/components/campaign/platform-connection-banner';
import { organizationsQueryOptions } from '@/lib/query-options';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  campaignCreationPreSelectionAtom,
  organizationDataAtom,
  googleAdsAccountAtom,
  selectedNewCampaignPlatformAtom,
} from '@/store';

import googleAdsLogo from '/google-ads.svg';
import metaLogo from '/meta.png';

export const Route = createFileRoute('/_authenticated/new/_layout/campaign/')({
  component: SelectCampaignType,
});

enum PICKER_STEP {
  SELECT_CAMPAIGN_TYPE = 'SELECT_CAMPAIGN_TYPE',
  SELECT_GOOGLE_ADS_ACCOUNT = 'SELECT_GOOGLE_ADS_ACCOUNT',
  VERIFY_TRACKING = 'VERIFY_TRACKING',
}

/**
 * What a picker hands back on CONTINUE (#4654). The pickers no longer navigate themselves — they
 * report the chosen platform + wizard route so the parent can run the right pre-flight first:
 * a Google pick goes through account-selection + tracking (when no account is set) before the
 * wizard; a Meta pick goes straight to ``/new/campaign/meta``, whose own connection + Pixel gate
 * (#4652) runs after this picker. Type selection therefore always comes first.
 */
type PickerContinue =
  | { platform: 'meta'; to: '/new/campaign/meta'; phase: 'TESTING' | 'SCALING' | 'RETARGETING' }
  | { platform: 'google'; to: string };

export function SelectCampaignType() {
  const navigate = useNavigate();
  const host = useWizardHost();
  const organizationData = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const setGoogleAdsAccount = useSetAtom(googleAdsAccountAtom);

  const { data: organizations } = useSuspenseQuery(organizationsQueryOptions());
  const currentOrgMembership = organizations?.find((org) => org.id === organizationData?.id)?.membership;
  const hasSelectedGoogleAdsAccount = !!(
    currentOrgMembership?.last_selected_googleads_account_id || googleAdsAccount
  );

  // The Google Ads account picker never auto-opens on entry. Connected with no account is a
  // legitimate state (a Meta-only organisation, say) and the #5907 auto-open met a user coming
  // to build a Meta campaign with a Google account list. The type-then-account pre-flight below
  // catches an unselected Google account once Google is actually picked.

  // Type selection always comes first now (#4654). Pre-flight checks fire *after* the user picks a
  // platform, scoped to that platform: a Google pick that has no account routes through
  // account-selection + tracking here; a Meta pick goes straight to its wizard (which runs its own
  // connection + Pixel gate). The Google wizards assume an account is already selected, so that
  // pre-flight must complete before navigating to them — hence it lives here, not in the wizard.
  const [currentStep, setCurrentStep] = useState<PICKER_STEP>(PICKER_STEP.SELECT_CAMPAIGN_TYPE);
  const [stepValid, setStepValid] = useState(false);
  const [isWarningErrorSuccess, setIsWarningErrorSuccess] = useState(false);
  // The Google wizard route the user is headed for, captured when they pick a Google type without an
  // account — navigated to once account-selection + tracking complete.
  const [pendingGoogleRoute, setPendingGoogleRoute] = useState<string | null>(null);

  const setStepValidCallback = useCallback((valid: boolean) => setStepValid(valid), []);
  const setIsWarningErrorSuccessCallback = useCallback((isWarning: boolean) => setIsWarningErrorSuccess(isWarning), []);

  // Clear atom when re-entering account selection so user picks fresh
  useEffect(() => {
    if (currentStep === PICKER_STEP.SELECT_GOOGLE_ADS_ACCOUNT) {
      setGoogleAdsAccount(null);
    }
  }, [currentStep, setGoogleAdsAccount]);

  // CONTINUE from the type picker. Meta navigates straight to its wizard (Meta pre-flight lives
  // there). Google navigates directly when an account is already selected, else enters the Google
  // account + tracking pre-flight, remembering the wizard route to resume to afterwards.
  const handlePickerContinue = (target: PickerContinue) => {
    if (target.platform === 'meta') {
      // The Meta wizard isn't hosted in the modal — close it and use the
      // full-page flow (its connection + Pixel gate lives on that route).
      host?.onLeave();
      navigate({ to: target.to, search: { phase: target.phase } });
      return;
    }
    if (hasSelectedGoogleAdsAccount) {
      if (host) {
        host.openWizard(target.to);
        return;
      }
      navigate({ to: target.to });
      return;
    }
    setPendingGoogleRoute(target.to);
    setStepValid(false);
    setIsWarningErrorSuccess(false);
    setCurrentStep(PICKER_STEP.SELECT_GOOGLE_ADS_ACCOUNT);
  };

  // Account + tracking are done — go to the Google wizard the user originally picked.
  const finishGooglePreflight = () => {
    if (!pendingGoogleRoute) return;
    if (host) {
      host.openWizard(pendingGoogleRoute);
    } else {
      navigate({ to: pendingGoogleRoute });
    }
  };

  if (currentStep === PICKER_STEP.SELECT_CAMPAIGN_TYPE) {
    return <PlatformPicker onContinue={handlePickerContinue} />;
  }

  const isAccountStep = currentStep === PICKER_STEP.SELECT_GOOGLE_ADS_ACCOUNT;

  return (
    <div className="absolute inset-0 overflow-y-auto">
      <div className="max-w-3xl w-full space-y-6 mx-auto pt-8 pb-12 px-4">
        <h1 className="text-3xl font-bold text-center mb-8">
          {isAccountStep ? 'Select Google Ads Account' : 'Verify Conversion Tracking'}
        </h1>
        <Card className={isWarningErrorSuccess ? 'p-0' : 'p-6'}>
          {isAccountStep && (
            <SelectGoogleAdsAccount
              setCurrentStep={(step) => setCurrentStep(step as unknown as PICKER_STEP)}
              setStepValid={setStepValidCallback}
              setIsWarningErrorSuccess={setIsWarningErrorSuccessCallback}
            />
          )}
          {currentStep === PICKER_STEP.VERIFY_TRACKING && (
            <TrackingIssues
              onNext={finishGooglePreflight}
              setStepValid={setStepValidCallback}
              setIsWarningErrorSuccess={setIsWarningErrorSuccessCallback}
            />
          )}
        </Card>
        <div className="flex justify-between items-center pt-6">
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              setIsWarningErrorSuccess(false);
              if (isAccountStep) {
                // Back to the type picker — it now precedes account selection.
                setStepValid(false);
                setPendingGoogleRoute(null);
                setCurrentStep(PICKER_STEP.SELECT_CAMPAIGN_TYPE);
              } else {
                setCurrentStep(PICKER_STEP.SELECT_GOOGLE_ADS_ACCOUNT);
              }
            }}
            className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>BACK</span>
          </Button>
          <Button
            size="lg"
            disabled={!stepValid}
            onClick={() => {
              setIsWarningErrorSuccess(false);
              if (isAccountStep) {
                setStepValid(false);
                setCurrentStep(PICKER_STEP.VERIFY_TRACKING);
              } else {
                finishGooglePreflight();
              }
            }}
            className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
          >
            <span>CONTINUE</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Platform-first picker ──────────────────────────────────────────────────────
// Platform-first picker (#3817 Meta UX): a Google / Meta tab bar over the campaign-type list,
// single-select via radio. The only picker since Meta went GA (#5976) — the Google-only
// ``ProdPicker`` it replaced was removed with the gate.

/**
 * Demo-aligned concept ids. Independent of ``CAMPAIGN_TYPE`` because that enum still describes a
 * Google campaign goal (and would force us to fold the Meta-only ``retargeting`` row into the same
 * enum, which would pollute every Google call site).
 */
type RowId = CampaignTypeRowId;
type PlatformId = 'google' | 'meta';


/**
 * Campaign types offered per platform. Google keeps the full prod set plus the test-mode-only ICP /
 * Location rows; Meta mirrors the backend phase mapping in ``DISPATCH`` — Prospecting, HiPo,
 * Retargeting (no Brand or Competitor campaign exists on Meta).
 */
const TYPES_BY_PLATFORM: Record<PlatformId, RowId[]> = {
  google: ['brand', 'prospecting', 'hipo', 'competitor', 'icp', 'location'],
  meta: ['prospecting', 'hipo', 'retargeting'],
};

/**
 * ICP and Location are not offered to clients yet (#6714). Both wizards work and both routes stay
 * reachable, so anything already linking to one keeps working; they are simply not among the types
 * a client is invited to create. Test mode shows them, as it does every unreleased surface.
 */
const TEST_MODE_ONLY_ROWS: readonly RowId[] = ['icp', 'location'];

const PLATFORM_LOGO: Record<PlatformId, string> = {
  google: googleAdsLogo,
  meta: metaLogo,
};

type DispatchTarget =
  | { to: '/new/campaign/brand' }
  | { to: '/new/campaign/prospecting' }
  | { to: '/new/campaign/competitor' }
  | { to: '/new/campaign/hipo' }
  | { to: '/new/campaign/icp' }
  | { to: '/new/campaign/location' }
  | { to: '/new/campaign/meta'; phase: 'TESTING' | 'SCALING' | 'RETARGETING' };

/** Concept × platform → wizard route. The Meta phase mapping is the only translation step. */
const DISPATCH: Partial<Record<`${RowId}:${PlatformId}`, DispatchTarget>> = {
  'prospecting:google': { to: '/new/campaign/prospecting' },
  'prospecting:meta': { to: '/new/campaign/meta', phase: 'TESTING' },
  'hipo:google': { to: '/new/campaign/hipo' },
  'hipo:meta': { to: '/new/campaign/meta', phase: 'SCALING' },
  'retargeting:meta': { to: '/new/campaign/meta', phase: 'RETARGETING' },
  'brand:google': { to: '/new/campaign/brand' },
  'competitor:google': { to: '/new/campaign/competitor' },
  'icp:google': { to: '/new/campaign/icp' },
  'location:google': { to: '/new/campaign/location' },
};

/** Last-resort tab for a restored row when neither the hash nor the platform atom is set
 * (e.g. a pre-selection written by /competitors or /add-to-campaign, #5975). Retargeting is
 * Meta-only; shared concepts default to Google to match the Google-priority ordering elsewhere. */
function platformForRow(row: RowId): PlatformId {
  return TYPES_BY_PLATFORM.google.includes(row) ? 'google' : 'meta';
}

function PlatformPicker({ onContinue }: { onContinue: (target: PickerContinue) => void }) {
  const navigate = useNavigate();
  const host = useWizardHost();
  const availablePlatforms: PlatformId[] = ['google', 'meta'];
  const [preSelection, setPreSelection] = useAtom(campaignCreationPreSelectionAtom);
  // Per-platform connection gating (#5937): a disconnected platform's tab stays visible and its
  // type list renders, but the rows are unselectable and the connect banner explains why. Both
  // disconnected → both tabs gated; the create option itself is never hidden.
  const { googleConnected, googleBroken, metaConnected, isLoading: isLoadingConnections } = usePlatformConnections();
  const isTestMode = useTestMode();
  const typesFor = (platform: PlatformId): RowId[] =>
    TYPES_BY_PLATFORM[platform].filter((row) => isTestMode || !TEST_MODE_ONLY_ROWS.includes(row));
  const isPlatformDisconnected = (platform: PlatformId) =>
    !isLoadingConnections && (platform === 'google' ? !googleConnected : !metaConnected);

  // Landing back here right after Meta OAuth (#5907: the callback returns the user to the page
  // they connected from), the integration is connected but no ad account is selected yet — the
  // Meta tab would still be gated with nowhere to go. Auto-open the ad-account picker exactly
  // like Settings > Integrations does; picking flips metaConnected and un-gates the tab. Ref
  // guards against reopening after a manual dismiss in the same session.
  const { data: metaStatus } = useMetaIntegrationStatus();
  const [isOpenMetaAccountPicker, setIsOpenMetaAccountPicker] = useState(false);
  const hasAutoOpenedMetaPicker = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedMetaPicker.current) return;
    if (shouldAutoOpenMetaAccountPicker(metaStatus)) {
      hasAutoOpenedMetaPicker.current = true;
      setIsOpenMetaAccountPicker(true);
    }
  }, [metaStatus]);

  // Restore the row from the atom (round-trip from a wizard's Back button) and re-derive its tab.
  const initialRow = (() => {
    const prior = preSelection.types[0];
    if (typeof prior === 'string' && (Object.keys(TYPE_META) as RowId[]).includes(prior as RowId)) {
      return prior as RowId;
    }
    return null;
  })();

  // The active platform tab is mirrored into the URL hash (the repo's tab-persistence
  // mechanism) so Meta's full-page OAuth round trip restores the tab the user connected from
  // (#5907) — the callback carries pathname + hash back here. Google's popup flow never
  // reloads, so its tab survives on its own. The atom is the in-session fallback (#5975):
  // a wizard's Back button navigates without the hash, and the row id alone can't restore
  // the tab because Prospecting/HiPo exist on both platforms.
  const [savedPlatform, setSavedPlatform] = useAtom(selectedNewCampaignPlatformAtom);
  const hashPlatform = ((): PlatformId | null => {
    const raw = window.location.hash.replace('#', '');
    return raw === 'google' || raw === 'meta' ? raw : null;
  })();
  const restoredPlatform = hashPlatform ?? savedPlatform;
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformId>(
    () => restoredPlatform ?? (initialRow ? platformForRow(initialRow) : 'google'),
  );
  const [selectedType, setSelectedType] = useState<RowId | null>(initialRow);
  const [hasInitialized, setHasInitialized] = useState(initialRow !== null);

  // Default to Brand on Google during creation (#4808). A selection restored from the atom
  // already sets hasInitialized, so this only fires on a fresh entry. Waits out the connection
  // signals so the default lands on a connected platform (#5937): Google when connected, else
  // Meta's first offering; nothing is pre-selected when both are disconnected. A restored tab
  // (OAuth-return hash or in-session atom, #5975) is the user's explicit context — keep their
  // platform and only default the type within it.
  useEffect(() => {
    if (hasInitialized || isLoadingConnections) return;
    if (restoredPlatform) {
      if (restoredPlatform === 'meta' && metaConnected) setSelectedType('prospecting');
      if (restoredPlatform === 'google' && googleConnected) setSelectedType('brand');
    } else if (googleConnected) {
      setSelectedPlatform('google');
      setSelectedType('brand');
    } else if (metaConnected) {
      setSelectedPlatform('meta');
      setSelectedType('prospecting');
    }
    setHasInitialized(true);
  }, [hasInitialized, isLoadingConnections, googleConnected, metaConnected, restoredPlatform]);

  const handleSwitchPlatform = (platform: PlatformId) => {
    setSelectedPlatform(platform);
    setSavedPlatform(platform);
    // Mirror the tab into the hash (replace: no history spam) so an OAuth round trip started
    // from this tab lands back on it. Modal-hosted, the page underneath owns the hash — the
    // atom above is the only persistence needed there.
    if (!host) {
      navigate({ to: '/new/campaign', hash: platform, replace: true });
    }
    // Keep the selection if the new tab also offers it (Prospecting / HiPo); otherwise clear it.
    // A disconnected tab never keeps a selection — its rows are unselectable (#5937).
    setSelectedType((prev) =>
      prev && !isPlatformDisconnected(platform) && typesFor(platform).includes(prev) ? prev : null,
    );
  };

  const handleBack = () => {
    if (host) {
      host.onExit();
    } else {
      navigate({ to: '/campaigns' });
    }
  };

  const handleContinue = () => {
    if (!selectedType || isPlatformDisconnected(selectedPlatform)) return;
    const target = DISPATCH[`${selectedType}:${selectedPlatform}` as const];
    if (!target) return;

    setPreSelection((prev) => ({ ...prev, types: [selectedType] }));
    // Persist the platform alongside the row (#5975) so a wizard's Back button restores this
    // tab — the user may never have switched tabs, so the atom can't rely on handleSwitchPlatform.
    setSavedPlatform(selectedPlatform);

    // Hand the chosen platform + route up; the parent runs the platform-scoped pre-flight (#4654).
    if (target.to === '/new/campaign/meta') {
      onContinue({ platform: 'meta', to: target.to, phase: target.phase });
    } else {
      onContinue({ platform: 'google', to: target.to });
    }
  };

  return (
    <div className="absolute inset-0 overflow-y-auto p-8">
      <div className="max-w-3xl w-full space-y-6 mx-auto">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Create Campaign</h1>
          <p className="text-gray-500">Pick a platform, then the campaign type to launch.</p>
        </div>

        <TabCardPanel
          tabs={availablePlatforms.map((platform) => ({
            id: platform,
            label: platform === 'google' ? 'Google' : 'Meta',
            render: () => (
              <button
                type="button"
                onClick={() => handleSwitchPlatform(platform)}
                className={`${tabButtonClass(selectedPlatform === platform)} flex items-center gap-2 cursor-pointer`}
              >
                {/* object-contain: Meta's mark is wider than tall — a bare h-4 w-4 squishes it. */}
                <img src={PLATFORM_LOGO[platform]} alt="" className="h-4 w-4 object-contain" />
                {platform === 'google' ? 'Google' : 'Meta'}
              </button>
            ),
          }))}
        >
          <motion.div
            key={selectedPlatform}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="space-y-3"
          >
            {isPlatformDisconnected(selectedPlatform) && (
              <PlatformConnectionBanner
                platform={selectedPlatform}
                broken={selectedPlatform === 'google' && googleBroken}
              />
            )}
            {typesFor(selectedPlatform).map((rowId) => (
              <CampaignTypeOption
                key={rowId}
                meta={TYPE_META[rowId]}
                platform={selectedPlatform}
                isSelected={selectedType === rowId}
                disabled={isPlatformDisconnected(selectedPlatform)}
                onSelect={() => setSelectedType(rowId)}
              />
            ))}
          </motion.div>
        </TabCardPanel>

        <div className="flex justify-between items-center pt-6">
          <Button
            variant="outline"
            size="lg"
            onClick={handleBack}
            className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>BACK</span>
          </Button>

          <Button
            size="lg"
            disabled={!selectedType}
            onClick={handleContinue}
            className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
          >
            <span>CONTINUE</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Auto-opens right after the Meta OAuth callback returns the user here (#5907) — the same
            picker Settings auto-opens. Closing without picking is allowed; the Meta tab stays
            gated until an ad account is selected. */}
        <MetaAdAccountSelectionModal
          isOpen={isOpenMetaAccountPicker}
          onClose={() => setIsOpenMetaAccountPicker(false)}
        />
      </div>
    </div>
  );
}

/**
 * One selectable campaign-type row in the test-mode picker: radio + type icon (with a small platform
 * badge in the corner) + label/description. Single-select; highlights Cobalt when chosen.
 */
